import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Moon, Sun, LogOut, User, ShieldCheck } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Crest, Wordmark } from "@/components/Logo";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const BASE_NAV = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Gallery" },
  { to: "/knowledge", label: "Knowledge" },
  { to: "/events", label: "Events" },
  { to: "/helpdesk", label: "Help Desk" },
];

// Group Insurance nav visibility is admin-controlled (campaign settings); cached per session
let _giNavCache = null;
async function fetchGiNav() {
  if (_giNavCache !== null) return _giNavCache;
  try {
    const { default: api } = await import("@/lib/api");
    const { data } = await api.get("/insurance/campaign");
    _giNavCache = !!(data.enabled && data.show_in_nav);
  } catch {
    _giNavCache = false;
  }
  return _giNavCache;
}

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [giNav, setGiNav] = React.useState(false);
  React.useEffect(() => { fetchGiNav().then(setGiNav); }, []);
  const NAV = React.useMemo(() => {
    const nav = [...BASE_NAV];
    if (user && typeof user === "object" && isAdminRole(user.role)) {
      // Directory is restricted: only super admin, state & district office bearers.
      nav.splice(1, 0, { to: "/directory", label: "Directory" });
    }
    if (giNav) nav.splice(1, 0, { to: "/#group-insurance", label: "Group Insurance", icon: true });
    return nav;
  }, [user, giNav]);

  return (
    <header className="sticky top-0 z-50">
      <div className="h-1 gov-stripe" />
      <motion.div
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="glass border-b border-border/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <Crest size={44} />
            <Wordmark className="hidden sm:block h-10 w-auto" />
            <div className="hidden md:block leading-tight pl-1 border-l border-border/60 ml-1">
              <div className="font-black tracking-tight text-foreground text-lg">KELTA</div>
              <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Kerala Local Body Technical Assistants Association</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(n => (
              <Link
                key={n.to + n.label}
                to={n.to}
                onClick={n.to.includes("#") && loc.pathname === "/" ? (e) => {
                  e.preventDefault();
                  document.getElementById(n.to.split("#")[1])?.scrollIntoView({ behavior: "smooth" });
                } : undefined}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  loc.pathname === n.to ? "text-brand-blue dark:text-primary" : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon"
              onClick={toggle}
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {user && typeof user === "object" ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu-btn" className="gap-2">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline truncate max-w-[120px]">
                      {user.full_name || user.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-xs text-muted-foreground">Signed in as</div>
                    <div className="truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.role && user.role !== "member" ? (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="menu-admin-link">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" data-testid="menu-dashboard-link">
                        <User className="w-4 h-4 mr-2" /> My Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="nav-login-btn">Login</Button>
                </Link>
                <Link to="/register" className="hidden sm:block">
                  <Button size="sm" className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="nav-join-btn">
                    Join KELTA
                  </Button>
                </Link>
              </>
            )}

            <Button
              variant="ghost" size="icon" className="lg:hidden"
              onClick={() => setOpen(v => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-border/40 px-4 py-3 space-y-1">
            {NAV.map(n => (
              <Link
                key={n.to + n.label}
                to={n.to}
                onClick={n.to.includes("#") && loc.pathname === "/" ? (e) => {
                  e.preventDefault();
                  setOpen(false);
                  document.getElementById(n.to.split("#")[1])?.scrollIntoView({ behavior: "smooth" });
                } : () => setOpen(false)}
                className="block px-3 py-2 rounded-md text-sm font-medium hover:bg-muted"
              >
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </motion.div>
    </header>
  );
}

function Logo() {
  return <Crest size={40} />;
}
